"use client"

import type { ReactNode } from "react"

import { useLocale, useTranslations } from "next-intl"

import { Button, Cents, Row, ShieldCheckIcon, Stack, Surface, UndoIcon } from "@/components/ui"
import { resolveSupportedLocale } from "@/i18n/locales"
import { addMoney, formatMoneyWithCode, isPositiveMoney, type Money } from "@/lib/money"

interface OrderSummaryProps {
  // What the lines actually cost, discounts already applied. The subtotal row
  // adds `savings` back so the three figures reconcile down the panel: a total
  // equal to the subtotal with a discount subtracted between them reads broken.
  total: Money
  count: number
  savings: Money
  installment?: Money
  installments?: number
  lineCount?: number
  freeShippingLines?: number
  outOfStock?: boolean
  onCheckout?: () => void
}

const TrustRow = ({ icon, className, children }: { icon: ReactNode; className?: string; children: ReactNode }) => (
  <Row gap="150" align="start" className={className} data-testid="trust-row">
    <span aria-hidden className="mt-50 shrink-0">
      {icon}
    </span>
    <span className="min-w-0">{children}</span>
  </Row>
)

export const OrderSummary = ({ total, count, savings, installment, installments = 4, lineCount = 0, freeShippingLines = 0, outOfStock = false, onCheckout }: OrderSummaryProps) => {
  const t = useTranslations("cart")
  const locale = resolveSupportedLocale(useLocale())
  // Free shipping is a per-product discount rule, not an order threshold, and the
  // real figure only exists once checkout has an address and a method. Promising
  // "Gratis" on every cart is a promise checkout can take back.
  const allFree = lineCount > 0 && freeShippingLines === lineCount
  const someFree = freeShippingLines > 0 && !allFree
  return (
    <Stack gap="350" data-testid="order-summary" className="@5xl:gap-500">
      <Surface padding="none" className="rounded-12x border-line-faint shadow-panel border p-500 @5xl:p-750">
        <Stack gap="0" className="font-text text-body-xs text-fg @5xl:text-body @5xl:gap-125 @5xl:leading-4.75">
          <Row justify="between" gap="300">
            <span>{t("subtotalWithCount", { count })}</span>
            <span className="tabular-nums">
              <Cents>{formatMoneyWithCode(addMoney(total, savings), locale)}</Cents>
            </span>
          </Row>
          {isPositiveMoney(savings) && (
            <Row justify="between" gap="300">
              <span>{t("discount")}</span>
              <span className="text-fg-success-deep tabular-nums">
                <Cents>{`−${formatMoneyWithCode(savings, locale)}`}</Cents>
              </span>
            </Row>
          )}
          <Row justify="between" gap="300">
            <span>{t("shipping")}</span>
            {allFree && <span className="text-fg-success-deep">{t("free")}</span>}
            {someFree && <span className="text-fg-success-deep">{t("freeOnSome", { count: freeShippingLines })}</span>}
            {!allFree && !someFree && <span className="text-fg-muted">{t("shippingAtCheckout")}</span>}
          </Row>
        </Stack>
        <Stack gap="0" className="font-text text-brand mt-350 tabular-nums @5xl:mt-500">
          <span className="text-body-lg @5xl:text-heading-sm font-semibold">
            <Cents>{t("totalWithAmount", { amount: formatMoneyWithCode(total, locale) })}</Cents>
          </span>
          {installment != null && (
            <span className="text-body-xs @5xl:text-body font-medium @5xl:leading-4.75">
              <Cents>{t("orUpToInstallments", { count: installments, amount: formatMoneyWithCode(installment, locale) })}</Cents>
            </span>
          )}
        </Stack>
        <Button fullWidth className="shadow-cta mt-750 hidden @3xl:flex" disabled={outOfStock} onClick={onCheckout}>
          {outOfStock ? t("outOfStock") : t("continueToPayment")}
        </Button>
      </Surface>
      <Stack gap="0" className="font-text text-body-xs text-fg @5xl:text-body @5xl:leading-4.75">
        <TrustRow icon={<UndoIcon size={15} className="text-fg-brand" />}>
          {t.rich("trustReturns", { accent: (chunks) => <span className="text-fg-brand">{chunks}</span> })}
        </TrustRow>
        <TrustRow icon={<ShieldCheckIcon size={15} />}>{t("trustProtected")}</TrustRow>
      </Stack>
    </Stack>
  )
}
